// Admin only: approve accounts, set roles, clear the local copy (or wipe demo data).
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useAuth } from '../auth/AuthProvider';
import { roleKey } from '../components/Nav';
import { useUi } from '../components/Ui';
import { config, isDemo } from '../config';
import type { Profile, Role } from '../domain/types';
import { useI18n } from '../i18n/I18nProvider';
import { clearAppointmentCache } from '../lib/appointments';
import { db } from '../lib/db';
import { qk, useProfiles } from '../lib/queries';

const ROLES: Role[] = ['viewer', 'collector', 'planner', 'admin'];

export function TeamPage() {
  const { t, fmtDate } = useI18n();
  const { user } = useAuth();
  const ui = useUi();
  const qc = useQueryClient();
  const profiles = useProfiles();
  const people = profiles.data || [];

  useEffect(() => {
    document.title = t('tmTitle') + ' · ' + config.siteName;
  }, [t]);

  const update = async (id: string, patch: Partial<Pick<Profile, 'role' | 'approved'>>) => {
    try {
      await db.updateProfile(id, patch);
      ui.toast(t('updated'), 'ok');
    } catch (err) {
      ui.toast(t('saveFailed', { msg: (err as Error).message }), 'err');
    }
    await qc.invalidateQueries({ queryKey: qk.profiles });
  };
  const nWait = people.filter((p) => !p.approved).length;

  return (
    <div className="pg team">
      <div className="wrap">
        <h1>{t('tmTitle')}</h1>
        <div className="sub">{t('tmSub')}</div>
        <div className="roles">
          {(['roleAdmin', 'rolePlanner', 'roleViewer', 'roleCollector'] as const).map((r, i) => (
            <div key={r}>
              <b>{t(r)}</b>
              {t((['rAdmin', 'rPlanner', 'rViewer', 'rCollector'] as const)[i])}
            </div>
          ))}
        </div>
        <div className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
            <h2>{t('tmPeople')}</h2>
            <span className="note">{nWait ? t('tmPending', { n: nWait }) : ''}</span>
          </div>
          <div className="tbl-wrap">
            <table className="t">
              <thead>
                <tr>
                  <th>{t('cName')}</th>
                  <th>{t('cEmail')}</th>
                  <th>{t('cSince')}</th>
                  <th>{t('cRole')}</th>
                  <th>{t('cAccess')}</th>
                </tr>
              </thead>
              <tbody>
                {people.map((p) => {
                  const me = p.id === user?.id;
                  return (
                    <tr key={p.id}>
                      <td>
                        {p.full_name || '—'} {me && <span className="tag mute">{t('you')}</span>}
                      </td>
                      <td>{p.email || ''}</td>
                      <td className="mono">{fmtDate(p.created_at)}</td>
                      <td>
                        <select value={p.role} disabled={me} onChange={(e) => void update(p.id, { role: e.target.value as Role })}>
                          {ROLES.map((r) => (
                            <option key={r} value={r}>
                              {t(roleKey(r))}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td>
                        {p.approved ? (
                          <>
                            <span className="tag ok">{t('approved')}</span>{' '}
                            {!me && (
                              <button className="small danger" onClick={() => void update(p.id, { approved: false })}>
                                {t('revoke')}
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            <span className="tag warn">{t('waiting')}</span>{' '}
                            <button className="small primary" onClick={() => void update(p.id, { approved: true })}>
                              {t('approve')}
                            </button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="card">
          <h2>{t('tmBrowser')}</h2>
          <div className="note" style={{ marginBottom: 10 }}>
            {t('tmBrowserSub')}
          </div>
          <div className="uco-row">
            {!isDemo && (
              <button
                onClick={async () => {
                  if (user) await clearAppointmentCache(user.id);
                  await qc.invalidateQueries({ queryKey: ['appointments'] });
                  ui.toast(t('cleared'), 'ok');
                }}
              >
                {t('clearCache')}
              </button>
            )}
            {isDemo && (
              <button
                className="danger"
                onClick={async () => {
                  if (!confirm(t('wipeConfirm'))) return;
                  await db.wipe?.();
                  qc.clear();
                  ui.toast(t('wiped'), 'ok');
                }}
              >
                {t('wipeDemo')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
